import { QueryClient } from "@tanstack/react-query";
import { PostHog } from "posthog-js";
import { RouteObject } from "react-router";
import { participantCreateAccountViewLoader } from "~/participant/views/ParticipantCreateAccountView/ParticipantCreateAccountView.loader";
import { participantJoinVerifyLoader } from "~/participant/views/ParticipantJoinVerify/ParticipantJoinVerify.loader";
import { ParticipantJoinViewErrorHandler } from "~/participant/views/ParticipantJoinView/ParticipantJoinView.error";
import { participantJoinViewLoader } from "~/participant/views/ParticipantJoinView/ParticipantJoinView.loader";
import { TopLevelRouteErrorHandler } from "~/shared/components/errors/Error";
import { lazyRetry } from "~/shared/util/lazyRetry";
import { loginLoader } from "~/shared/views/LoginView/LoginView.loader";
import { twoFactorAuthLoader } from "~/shared/views/TwoFactorAuthView/TwoFactorAuthView.loader";

export function getSharedRoutes(queryClient: QueryClient, posthogClient?: PostHog): Array<RouteObject> {
  return [
    // RESET PASSWORD
    {
      path: "reset-password",
      errorElement: <TopLevelRouteErrorHandler />,
      lazy: async () => {
        const { ResetPasswordRequestView } = await lazyRetry(
          () => import("~/shared/views/ResetPasswordRequestView/ResetPasswordRequestView"),
          "ResetPasswordRequestView",
        );
        return {
          Component: ResetPasswordRequestView,
        };
      },
    },
    {
      path: "reset-password/:resetToken",
      errorElement: <TopLevelRouteErrorHandler />,
      lazy: async () => {
        const { ResetPasswordView } = await lazyRetry(
          () => import("~/shared/views/ResetPasswordView/ResetPasswordView"),
          "ResetPasswordView",
        );
        return {
          Component: ResetPasswordView,
        };
      },
    },
    // SETUP
    {
      path: "setup/:resetToken",
      errorElement: <TopLevelRouteErrorHandler />,
      lazy: async () => {
        const { SetupView } = await lazyRetry(() => import("~/shared/views/SetupView"), "SetupView");
        return {
          Component: SetupView,
        };
      },
    },
    {
      path: "join/member/create-account",
      errorElement: <TopLevelRouteErrorHandler />,
      loader: (args) => {
        return participantCreateAccountViewLoader(args, posthogClient);
      },
      lazy: async () => {
        const { ParticipantCreateAccountView } = await lazyRetry(
          () => import("~/participant/views/ParticipantCreateAccountView/ParticipantCreateAccountView"),
          "ParticipantCreateAccountView",
        );
        return {
          Component: ParticipantCreateAccountView,
        };
      },
    },
    {
      path: "join/member/:token",
      errorElement: <ParticipantJoinViewErrorHandler />,
      loader: (args) => {
        return participantJoinViewLoader(args, queryClient, posthogClient);
      },
      lazy: async () => {
        const { ParticipantJoinLayout } = await lazyRetry(
          () => import("~/participant/views/ParticipantJoinView/ParticipantJoinLayout"),
          "ParticipantJoinLayout",
        );
        return {
          Component: ParticipantJoinLayout,
        };
      },
      children: [
        {
          index: true,
          lazy: async () => {
            const { ParticipantJoinView } = await lazyRetry(
              () => import("~/participant/views/ParticipantJoinView/ParticipantJoinView"),
              "ParticipantJoinView",
            );
            return {
              Component: ParticipantJoinView,
            };
          },
        },
        {
          path: "verify",
          loader: participantJoinVerifyLoader,
          lazy: async () => {
            const { ParticipantJoinVerifyView } = await lazyRetry(
              () => import("~/participant/views/ParticipantJoinVerify/ParticipantJoinVerify"),
              "ParticipantJoinVerifyView",
            );
            return {
              Component: ParticipantJoinVerifyView,
            };
          },
        },
      ],
    },

    // LOGIN
    {
      path: "login",
      loader: () => {
        return loginLoader(queryClient);
      },
      errorElement: <TopLevelRouteErrorHandler />,
      lazy: async () => {
        const { LoginView } = await lazyRetry(() => import("~/shared/views/LoginView/LoginView"), "LoginView");
        return {
          Component: LoginView,
        };
      },
    },

    // 2FA
    {
      path: "2fa",
      loader: (args) => {
        return twoFactorAuthLoader(args, queryClient, posthogClient);
      },
      errorElement: <TopLevelRouteErrorHandler />,
      lazy: async () => {
        const { TwoFactorAuthView } = await lazyRetry(
          () => import("~/shared/views/TwoFactorAuthView/TwoFactorAuthView"),
          "TwoFactorAuthView",
        );
        return {
          Component: TwoFactorAuthView,
        };
      },
    },
  ];
}
